function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

import React, { useMemo } from "react";
import PropTypes from "prop-types";
import LoadingIcon from "./LoadingIcon";
import useTheme from "../hooks/useTheme";
import useBackground, { mapResponsiveValues } from "../hooks/useBackground";
import useResponsivePropsCSS from "../hooks/useResponsivePropsCSS";
import { responsiveMarginType, responsiveWidthType } from "../hooks/useResponsiveProp";
import { hasOwnProperty } from "../utils/core";
import { getPropsFromMap } from "../utils/component";
import { responsiveMargin, responsiveSize } from "../utils/css";
import { mergeProps } from "../utils/component";
import { formatArray } from "../utils/array";
import { jsx as ___EmotionJSX } from "@emotion/core";
var VARIANTS = ["primary", "secondary", "icon"];
var COLORS = ["highlight.blue.t100", "white", "black", "green"];
var TYPES = ["button", "submit"];
var DEFAULT_PROPS = {
  variant: "primary",
  color: "highlight.blue.t100",
  loading: false,
  disabled: false,
  type: "button",
  __internal__keyboardFocus: false,
  __internal__hover: false,
  __internal__active: false
};
Button.VARIANTS = VARIANTS;
Button.COLORS = COLORS;
Button.TYPES = TYPES;
Button.DEFAULT_PROPS = DEFAULT_PROPS;
var darkColorsMap = {
  "primary.blue.t100": true,
  "highlight.blue.t100": true,
  "highlight.pink.t100": true,
  "highlight.purple.t100": true
};
var mediumColorsMap = {
  "grey.t07": true,
  "grey.t10": true,
  "grey.t16": true,
  "secondary.lightBlue.t25": true,
  "secondary.pink.t30": true,
  "secondary.pink.t15": true,
  "secondary.purple.t30": true,
  "secondary.purple.t15": true,
  "secondary.turquoise.t30": true,
  "secondary.turquoise.t10": true
};

function getButtonColor(bg) {
  return darkColorsMap[bg] ? "white" : mediumColorsMap[bg] ? "black" : "highlight.blue.t100";
}

function getLoadingIconColor(bg) {
  return getButtonColor(bg);
}

function Button(props) {
  var theme = useTheme();

  var _useBackground = useBackground(),
      bgMap = _useBackground.bgMap;

  var mergedProps = mergeProps(props, DEFAULT_PROPS, {}, {
    variant: function variant(_variant) {
      return VARIANTS.includes(_variant);
    },
    color: function color(_color) {
      return COLORS.includes(_color);
    },
    disabled: function disabled(_disabled) {
      return typeof _disabled === "boolean";
    },
    type: function type(_type) {
      return TYPES.includes(_type);
    }
  });
  var variant = mergedProps.variant,
      loading = mergedProps.loading,
      disabled = mergedProps.disabled,
      type = mergedProps.type,
      onClick = mergedProps.onClick,
      children = mergedProps.children,
      testId = mergedProps.testId,
      __internal__keyboardFocus = mergedProps.__internal__keyboardFocus,
      __internal__hover = mergedProps.__internal__hover,
      __internal__active = mergedProps.__internal__active;
  var showLoadingIcon = ["primary", "secondary"].includes(variant) && props.color !== "green" && loading;
  var buttonCSS = useResponsivePropsCSS(mergedProps, DEFAULT_PROPS, {
    color: function color(_, theme, bp) {
      var color = hasOwnProperty(props, "color") ? mergedProps.color : getButtonColor(bgMap === null || bgMap === void 0 ? void 0 : bgMap[bp]);

      if (color === "black" && variant !== "secondary" || color === "green" && variant !== "primary") {
        color = DEFAULT_PROPS.color;
      }

      return theme.button.getCSS({
        targetElement: "button",
        variant: variant,
        color: color,
        showLoadingIcon: showLoadingIcon,
        __internal__keyboardFocus: __internal__keyboardFocus,
        __internal__hover: __internal__hover,
        __internal__active: __internal__active
      });
    },
    margin: responsiveMargin,
    width: responsiveSize("width")
  });
  var loadingIconCSS = theme.button.getCSS({
    targetElement: "loadingIcon"
  });
  var contentCSS = theme.button.getCSS({
    targetElement: "content",
    showLoadingIcon: showLoadingIcon
  });
  var loadingIconColorProps = useMemo(function () {
    return getPropsFromMap("color", mapResponsiveValues(bgMap, function (bg) {
      return getLoadingIconColor(bg);
    }, theme));
  }, [bgMap, theme]);
  return ___EmotionJSX("button", {
    css: buttonCSS,
    disabled: showLoadingIcon || disabled,
    type: type,
    onClick: onClick,
    "data-testid": testId
  }, showLoadingIcon && ___EmotionJSX("span", {
    css: loadingIconCSS,
    "aria-hidden": "true"
  }, ___EmotionJSX(LoadingIcon, loadingIconColorProps)), ___EmotionJSX("span", {
    css: contentCSS
  }, children));
}

Button.propTypes = _objectSpread(_objectSpread(_objectSpread({}, responsiveMarginType), responsiveWidthType), {}, {
  variant: PropTypes.oneOf(VARIANTS),
  color: function color(props) {
    if (props.color === undefined) {
      return;
    }

    if (COLORS.includes(props.color) === false) {
      return new Error("Button: color=\"".concat(props.color, "\" is not supported. Must be one of: ").concat(formatArray(COLORS)));
    }

    if (props.variant === "primary" && ["highlight.blue.t100", "white", "green"].includes(props.color) === false) {
      return new Error("Button: variant=\"primary\" should be used only with these colors: ".concat(formatArray(["highlight.blue.t100", "white", "green"])));
    }
  },
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  type: PropTypes.oneOf(TYPES),
  onClick: PropTypes.func,
  children: PropTypes.node,
  testId: PropTypes.string,
  __internal__keyboardFocus: PropTypes.bool,
  __internal__hover: PropTypes.bool,
  __internal__active: PropTypes.bool
});
export default Button;